import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Scale, 
  Target as TargetIcon,
  TrendingDown,
  TrendingUp,
  Plus, 
  Minus, 
  Quote,
  Loader2,
  RefreshCw
} from "lucide-react";

const WeightGoal = () => {
  const [profile, setProfile] = useState({ weight: 0, target_weight: 0, goal: "" });
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [userId, setUserId] = useState(null);

  const quotes = [
    "Consistency is the bridge between goals and accomplishment.",
    "Focus on progress, not perfection.",
    "Success is the sum of small efforts repeated daily.",
    "Your only limit is you.", 
    "Consistency is the bridge between goals and accomplishment.", 
    "Do something today that your future self will thank you for.", 
    "It’s a slow process, but quitting won’t speed it up.",
     "Don't stop until you're proud.",
      "Focus on progress, not perfection."
  ];
  const [activeQuote] = useState(quotes[Math.floor(Math.random() * quotes.length)]);

  useEffect(() => {
    const initSession = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        fetchProfile(user.id);
        setupRealtimeSubscription(user.id);
      }
    };
    initSession();

    // Cleanup subscription on unmount
    return () => {
      supabase.channel(`realtime-weight`).unsubscribe();
    };
  }, []);

  const fetchProfile = async (id) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("weight, target_weight, goal")
      .eq("id", id)
      .single();
    if (!error && data) setProfile(data);
    setLoading(false);
  };

  // --- REAL-TIME ENGINE ---
  const setupRealtimeSubscription = (id) => {
    supabase
      .channel(`public:profiles:id=eq.${id}`)
      .on('postgres_changes', { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'profiles', 
          filter: `id=eq.${id}` 
      }, (payload) => {
        // Sync the state immediately when database changes
        setProfile(prev => ({
          ...prev,
          weight: payload.new.weight,
          target_weight: payload.new.target_weight,
          goal: payload.new.goal
        }));
      })
      .subscribe();
  };

  const handleWeightUpdate = async (adjustment) => {
    if (!userId || isUpdating) return;
    setIsUpdating(true);

    const newWeight = Math.round((profile.weight + adjustment) * 10) / 10;

    // Optimistic UI update (feels instant)
    setProfile(prev => ({ ...prev, weight: newWeight }));

    const { error } = await supabase
      .from("profiles")
      .update({ weight: newWeight })
      .eq("id", userId);

    if (error) {
      console.error("Update error:", error);
      fetchProfile(userId); // Revert on error
    }
    setIsUpdating(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
    </div>
  );

  const remaining = Math.abs(profile.weight - profile.target_weight).toFixed(1);
  const isLossGoal = profile.weight > profile.target_weight;
  const themeColor = isLossGoal ? "emerald" : "blue";

  return (
    <div className="min-h-screen bg-[#05070a] text-slate-200 p-6 font-sans">
      <div className="max-w-md mx-auto space-y-8">
        
        {/* --- Header & Live Status --- */}
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <h1 className="text-2xl font-black italic tracking-tighter text-white uppercase">Metrics</h1>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Live Sync Enabled</p>
            </div>
          </div>
          {isUpdating && <RefreshCw className="w-4 h-4 text-emerald-500 animate-spin" />}
        </div>

        {/* --- Weight Display Card --- */}
        <motion.div className="bg-slate-900/40 border border-white/5 rounded-[3rem] p-10 relative overflow-hidden shadow-2xl">
          <div className="text-center relative z-10">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] mb-6">Current Body Mass</p>
            <div className="flex justify-center items-baseline gap-2">
              <span className="text-7xl font-black text-white italic">{profile.weight}</span>
              <span className={`text-xl font-bold text-${themeColor}-500 italic uppercase`}>Kg</span>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/5 flex justify-around items-center">
               <div className="text-center">
                  <p className="text-[10px] font-bold text-slate-600 uppercase">Target</p>
                  <p className="text-lg font-bold text-white">{profile.target_weight}kg</p>
               </div>
               <div className="h-8 w-[1px] bg-white/5" />
               <div className="text-center">
                  <p className="text-[10px] font-bold text-slate-600 uppercase">Remaining</p>
                  <p className={`text-lg font-bold text-${themeColor}-400`}>{remaining}kg</p>
               </div>
            </div>
          </div>
        </motion.div>

        {/* --- Progress Diagram Concept --- */}
        {/*  */}

        {/* --- Controls --- */}
        <div className="grid grid-cols-3 gap-4">
          <button onClick={() => handleWeightUpdate(-1.0)} className="h-20 bg-slate-900 border border-white/5 rounded-3xl flex items-center justify-center active:scale-95 transition-transform">
            <Minus className="w-8 h-8 text-white" />
          </button>
          <div className="flex flex-col items-center justify-center opacity-40">
            <Scale className="w-5 h-5 mb-1" />
            <span className="text-[9px] font-bold uppercase tracking-tighter">Adjust</span>
          </div>
          <button onClick={() => handleWeightUpdate(1.0)} className="h-20 bg-slate-900 border border-white/5 rounded-3xl flex items-center justify-center active:scale-95 transition-transform">
            <Plus className="w-8 h-8 text-white" />
          </button>
        </div>

        {/* --- Quote Section --- */}
        <div className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 text-center relative overflow-hidden">
          <Quote className="absolute -top-2 -left-2 w-16 h-16 text-white/[0.03]" />
          <p className="text-slate-400 italic font-medium leading-relaxed italic text-sm">"{activeQuote}"</p>
        </div>
      </div>
    </div>
  );
};

export default WeightGoal;